import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import {
	TICKET_TABLE_COLUMN_WIDTHS,
	TICKET_TABLE_MIN_WIDTH_WITH_ACCOUNT,
	TICKET_TABLE_MIN_WIDTH_WITHOUT_ACCOUNT,
} from '../ticketTableColumns';

/**
 * Styles for the inbox's table layout — a databoard-style DataGrid of tickets with filter menus on
 * the column headers. Header typography mirrors the DataGrid's HEADER_CELL_TEXT so interactive
 * headers read like the static ones.
 */

// Row hover wash, matching the desk list rows (see inboxStyles).
const ROW_HOVER = THEME.colors.background.main;

export const ticketTableStyles = StyleSheet.create({
	tablePane: {
		height: '100%',
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.light,
	},
	// Anchors the bulk-actions overlay so selecting rows never shifts the header/table below.
	tableHeaderArea: {
		flex: 'none',
		position: 'relative',
	},
	viewsRow: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 4,
		padding: '12px 20px',
	},
	viewTab: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 7,
		height: 'auto',
		padding: '6px 11px',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
		':hover': { backgroundColor: ROW_HOVER },
	},
	viewTabActive: {
		backgroundColor: THEME.colors.primary.light,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	viewTabIcon: {
		width: 16,
		height: 16,
		flex: 'none',
		color: THEME.colors.text.light,
	},
	viewTabCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	tableScroll: {
		flex: 1,
		minHeight: 0,
		overflow: 'auto',
	},
	fixedTable: {
		tableLayout: 'fixed',
	},
	fixedTableWithAccount: {
		minWidth: TICKET_TABLE_MIN_WIDTH_WITH_ACCOUNT,
	},
	fixedTableWithoutAccount: {
		minWidth: TICKET_TABLE_MIN_WIDTH_WITHOUT_ACCOUNT,
	},
	colCheckbox: {
		width: TICKET_TABLE_COLUMN_WIDTHS.select,
	},
	colNumber: {
		width: TICKET_TABLE_COLUMN_WIDTHS.number,
	},
	colSubject: {
		width: TICKET_TABLE_COLUMN_WIDTHS.subject,
	},
	colContact: {
		width: TICKET_TABLE_COLUMN_WIDTHS.contact,
	},
	colAccount: {
		width: TICKET_TABLE_COLUMN_WIDTHS.account,
	},
	colStatus: {
		width: TICKET_TABLE_COLUMN_WIDTHS.status,
	},
	colPriority: {
		width: TICKET_TABLE_COLUMN_WIDTHS.priority,
	},
	colCategory: {
		width: TICKET_TABLE_COLUMN_WIDTHS.category,
	},
	colAssignee: {
		width: TICKET_TABLE_COLUMN_WIDTHS.assignee,
	},
	colTime: {
		width: TICKET_TABLE_COLUMN_WIDTHS.lastUpdate,
	},
	// Interactive header control (filter menu trigger / sort toggle) inside a <th>. Font metrics
	// copy DataGrid's HEADER_CELL_TEXT so the header row stays visually uniform.
	headerBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		height: 'auto',
		padding: 0,
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		fontWeight: 600,
		fontSize: 12,
		letterSpacing: '1.1px',
		textTransform: 'uppercase',
		color: 'inherit',
		':hover': { color: THEME.colors.primary.main, background: 'transparent' },
		':disabled': { cursor: 'default', color: 'inherit' },
	},
	headerBtnActive: {
		color: THEME.colors.primary.main,
	},
	// The active Contact value is user text (often an email) — keep its natural casing against the
	// uppercase header treatment, and truncate so long values stay inside the fixed column.
	headerBtnValue: {
		maxWidth: 96,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		textTransform: 'none',
		letterSpacing: 'normal',
	},
	contactFilterPopover: {
		width: 260,
	},
	contactFilterPanel: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		paddingTop: 6,
		paddingBottom: 6,
	},
	contactFilterClearBtn: {
		alignSelf: 'flex-start',
		height: 'auto',
		paddingTop: THEME.spacing.xs,
		paddingBottom: THEME.spacing.xs,
		paddingLeft: THEME.spacing.sm,
		paddingRight: THEME.spacing.sm,
		border: 'none',
		background: 'transparent',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': { background: 'transparent', color: THEME.colors.text.main },
	},
	headerChevronAsc: {
		transform: 'rotate(180deg)',
	},
	filterMenu: {
		minWidth: 170,
		padding: 5,
		display: 'flex',
		flexDirection: 'column',
		gap: 1,
	},
	filterMenuItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		width: '100%',
		height: 'auto',
		padding: '7px 10px',
		border: 'none',
		borderRadius: 7,
		background: 'transparent',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
		textAlign: 'left',
		':hover': { backgroundColor: ROW_HOVER },
	},
	// TicketSearchBar host overrides: inline at the right of the views row, without the desk bar's
	// framing. zIndex auto so the bulk-selection overlay covers it like the rest of the row.
	tableSearchBar: {
		marginLeft: 'auto',
		width: 300,
		minWidth: 200,
		padding: 0,
		borderBottom: 'none',
		zIndex: 'auto',
	},
	// Right-anchor the Search criteria popover under its toggle instead of spanning the bar.
	tableFilterPanel: {
		left: 'auto',
		right: 0,
		top: 'calc(100% + 6px)',
		width: 320,
	},
	searchCriteriaRow: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		padding: '0 20px 10px',
	},
	searchCriteriaText: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	clearFiltersBtn: {
		height: 'auto',
		padding: '4px 8px',
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': { color: THEME.colors.text.main, background: 'transparent' },
	},
	accountScope: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		minWidth: 0,
	},
	accountScopeEditing: {
		width: 320,
	},
	accountScopeButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 7,
		minWidth: 0,
		maxWidth: 300,
		height: 32,
		padding: '0 10px',
	},
	accountScopePrefix: {
		flex: 'none',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
	},
	accountScopeName: {
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	accountScopeClearBtn: {
		height: 32,
		padding: '0 8px',
		border: 'none',
		background: 'transparent',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': { background: 'transparent', color: THEME.colors.text.main },
	},
	accountComboboxInput: {
		width: '100%',
		height: 32,
	},
	accountComboboxContent: {
		width: 320,
	},
	// Overlays the views row while rows are selected (Gmail-style) so the table never jumps.
	selectionBar: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 10,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '0 20px',
		backgroundColor: THEME.colors.primary.light,
	},
	selectionCount: {
		marginRight: 4,
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	bulkMenuBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		height: 'auto',
		padding: '4px 10px',
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.light,
		cursor: 'pointer',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		':hover': { color: THEME.colors.primary.main, background: THEME.colors.background.light },
		':disabled': { cursor: 'default', opacity: 0.6 },
	},
	selectionClearBtn: {
		marginLeft: 'auto',
		height: 'auto',
		padding: '4px 8px',
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': { color: THEME.colors.text.main, background: 'transparent' },
	},
	actionError: {
		flex: 'none',
		padding: '6px 20px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.danger.dark,
	},
	row: {
		cursor: 'pointer',
		':hover': { backgroundColor: ROW_HOVER },
	},
	cellCheckbox: {
		width: 1, // Shrink to content.
		whiteSpace: 'nowrap',
	},
	cellStopClick: {
		display: 'inline-flex',
		alignItems: 'center',
		minWidth: 0,
		maxWidth: '100%',
	},
	cellTruncate: {
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	truncatedText: {
		display: 'block',
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	cellEditBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		maxWidth: '100%',
		minWidth: 0,
		height: 'auto',
		padding: 0,
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		font: 'inherit',
		color: 'inherit',
		textAlign: 'left',
		':hover': { background: 'transparent', color: THEME.colors.primary.main },
	},
	cellEditChevron: {
		flex: 'none',
		color: THEME.colors.text.light,
	},
	cellNumber: {
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap',
		width: 1, // Shrink-to-fit so the number column hugs its content.
	},
	subjectBtn: {
		display: 'block',
		maxWidth: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		height: 'auto',
		padding: 0,
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		font: 'inherit',
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		textAlign: 'left',
		':hover': { background: 'transparent', textDecoration: 'underline' },
	},
	cellMuted: {
		color: THEME.colors.text.light,
	},
	cellTime: {
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap',
	},
	tableState: {
		padding: 32,
		textAlign: 'center',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
});
